import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PATHS } from '../routes/paths';
import {
  FOOTER_BREAKPOINT,
  MOBILE_BREAKPOINT,
  convertToVwMob,
} from '../Utils/responsive';
import mainLogo from '../assets/Logo/main_logo.png';
import {
  MISSIONFUND_PRIVACY_POLICY_URL,
  MISSIONFUND_TERMS_OF_SERVICE_URL,
  MISSIONFUND_SUPPORT_URL,
} from '../Utils/missionFundLegalUrls';
import kakaoLogo from '../assets/Icon/footer/kakao_icon.png';
import callIcon from '../assets/Icon/footer/famicons_call-sharp.svg';
import emailIcon from '../assets/Icon/footer/ic_round-email.svg';

const KAKAO_CHANNEL_CHAT_URL = 'http://pf.kakao.com/_xkHknxd/chat';

const Footer: React.FC = () => {
  const { i18n, t } = useTranslation('translation');
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'ko').split('-')[0];
  const handleKakaoChannelInquiry = () => {
    window.open(KAKAO_CHANNEL_CHAT_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <FooterWrapper>
      <Inner>
        <TopRow>
          <BrandBlock>
            <Logo src={mainLogo} alt={t('common.footer.logoAlt')} />
            <BrandDetails>
              <div>{t('common.footer.brandLine')}</div>
              <div>{t('common.footer.addressKorea')}</div>
              <ContactInline>
                <ContactItem>
                  <ContactIcon src={callIcon} alt="" aria-hidden="true" />
                  <span>1899-0693</span>
                </ContactItem>
                <ContactItem>
                  <ContactIcon src={emailIcon} alt="" aria-hidden="true" />
                  <span>cs@missionfund.org</span>
                </ContactItem>
              </ContactInline>
              <div>{t('common.footer.addressUS')}</div>
            </BrandDetails>
          </BrandBlock>

          <NavGrid>
            <FooterColumn>
              <FooterHeading>{t('common.footer.servicesHeading')}</FooterHeading>
              <FooterNavLink to={PATHS.NEW_PROJECT}>{t('common.footer.createProject')}</FooterNavLink>
              <FooterExternalLink href={MISSIONFUND_SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                {t('common.footer.consultingApply')}
              </FooterExternalLink>
            </FooterColumn>
            <FooterColumn>
              <FooterHeading>{t('common.footer.privacyTerms')}</FooterHeading>
              <FooterPolicyLink
                href={MISSIONFUND_TERMS_OF_SERVICE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('common.footer.servicePolicy')}
              </FooterPolicyLink>
              <FooterPolicyLink
                href={MISSIONFUND_PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('common.footer.privacyPolicy')}
              </FooterPolicyLink>
            </FooterColumn>
            <KakaoColumn>
              <KakaoButton type="button" onClick={handleKakaoChannelInquiry}>
                <KakaoDot src={kakaoLogo} alt="Kakao" />
                {t('common.footer.kakaoChannelInquiry')}
              </KakaoButton>
            </KakaoColumn>
          </NavGrid>
        </TopRow>

        <Divider />

        <BottomRow>
          <Statement>
            {t('common.footer.statementKo')}
            <br />
            {t('common.footer.statementEn')}
          </Statement>
          <LangSelect
            aria-label="language"
            value={currentLang}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
          >
            <option value="ko">{t('translation:lang.ko')}</option>
            <option value="en">{t('translation:lang.en')}</option>
          </LangSelect>
        </BottomRow>
      </Inner>
    </FooterWrapper>
  );
};

const FooterWrapper = styled.footer`
  background: #EEF3F9;
  padding: 69px 32px 70px;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  position: relative;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: ${convertToVwMob(28)} ${convertToVwMob(16)} ${convertToVwMob(20)};
    height: auto;
  }
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 28px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: ${convertToVwMob(18)};
  }
`;

const TopRow = styled.div`
  display: grid;
  /* Move service/terms area left (match design) */
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.25fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
    gap: ${convertToVwMob(18)};
  }
`;

const BrandBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

const Logo = styled.img`
  height: 31px;
  object-fit: contain;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: ${convertToVwMob(38)};
  }
`;

const BrandDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  font-size: 12px; /* Caption/Regular12pt */
  line-height: 16px; /* 12px + 4px */
  color: ${({ theme }) => theme.colors.grayScale[70]};

  & > * + * {
    margin-top: 4px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 0;
    font-size: ${convertToVwMob(12)};
    line-height: ${convertToVwMob(16)};

    & > * + * {
      margin-top: ${convertToVwMob(4)};
    }
  }
`;

const ContactInline = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

const ContactItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const ContactIcon = styled.img`
  width: 12px;
  height: 12px;
  flex: 0 0 12px;
  display: inline-block;
  object-fit: contain;
`;

const NavGrid = styled.div`
  display: grid;
  grid-template-columns: max-content max-content 1fr;
  column-gap: 72px;
  row-gap: 20px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  /* Narrow desktop/tablet: prevent breaking by collapsing to one column */
  @media (max-width: ${FOOTER_BREAKPOINT}px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
    gap: ${convertToVwMob(14)};
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const KakaoColumn = styled(FooterColumn)`
  /* move button left: don't pin to far right */
  align-items: flex-start;
  justify-self: start;
  gap: 0;
`;

const FooterHeading = styled.div`
  ${({ theme }) => theme.typography.caption.bold16} /* Caption/Bold16pt */
  color: ${({ theme }) => theme.colors.grayScale[70]}; /* GreyScale/70 */
  line-height: 20px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: ${convertToVwMob(16)};
  }
`;

const footerServiceLinkStyles = `
  font-size: 16px;
  line-height: 20px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: ${convertToVwMob(16)};
  }
`;

const FooterNavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.grayScale[60]};
  ${footerServiceLinkStyles}

  &:hover {
    color: ${({ theme }) => theme.colors.grayScale[70]};
  }
`;

const FooterExternalLink = styled.a`
  color: ${({ theme }) => theme.colors.grayScale[60]};
  ${footerServiceLinkStyles}

  &:hover {
    color: ${({ theme }) => theme.colors.grayScale[70]};
  }
`;

const FooterPolicyLink = styled.a`
  font-size: 16px;
  line-height: 20px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grayScale[60]};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.grayScale[70]};
    text-decoration: underline;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: ${convertToVwMob(16)};
  }
`;

const KakaoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 186px;
  height: 36px;
  padding: 0 16px;
  box-sizing: border-box;
  border-radius: 18px;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[60]};
  background: transparent; /* Fill 없음: 푸터 배경색 노출 */
  color: ${({ theme }) => theme.colors.grayScale[70]};
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  max-width: 100%;
  white-space: normal;
  text-align: center;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: ${convertToVwMob(10)};
    width: ${convertToVwMob(186)};
    height: ${convertToVwMob(36)};
    padding: 0 ${convertToVwMob(14)};
    border-radius: ${convertToVwMob(18)};
    font-size: ${convertToVwMob(13)};
  }
`;

const KakaoDot = styled.img`
  width: 20px;
  height: 20px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: ${convertToVwMob(18)};
    height: ${convertToVwMob(18)};
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 0.3px solid ${({ theme }) => theme.colors.grayScale[70]};
  margin: 0;
`;

const BottomRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: ${convertToVwMob(12)};
  }
`;

const Statement = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  line-height: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: ${convertToVwMob(12)};
  }
`;

const LangSelect = styled.select`
  padding: 0 34px 0 12px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[40]};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.grayScale[80]};
  font-size: 12px;
  line-height: 34px;
  cursor: pointer;
  min-width: 96px;

  /* match design: custom caret */
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23929292' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: ${convertToVwMob(34)};
    min-width: ${convertToVwMob(96)};
    padding: 0 ${convertToVwMob(34)} 0 ${convertToVwMob(16)};
    border-radius: ${convertToVwMob(6)};
    font-size: ${convertToVwMob(12)};
    line-height: ${convertToVwMob(34)};
    background-position: right ${convertToVwMob(12)} center;
    background-size: ${convertToVwMob(16)} ${convertToVwMob(16)};
  }
`;

export default Footer;

